import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Container, Form } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import { ActionButtons, Loader, SectionHeader } from '../../common/components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  getAllSellAccountingGroupsAsync,
  selectSellAccountingGroups,
  selectSellVats,
  selectSellVatsLoadingStatus,
  AccountingsQueryFilters,
  SellVatDetailsData,
  updateSellVatAsync,
  getAllSellVatsAsync,
} from './accountingSlice';
import { LOADING_STATUS } from '../../common/constants';

const SellVatsDetails = () => {
  const backLinkUrl = '/accounting/sellvats';
  const params = useParams();
  const dispatch = useAppDispatch();
  const sellvats = useAppSelector(selectSellVats);
  const sellvatsLoadingStatus = useAppSelector(selectSellVatsLoadingStatus);
  const accountingGroups = useAppSelector(selectSellAccountingGroups);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filtering, setFiltering] = useState<AccountingsQueryFilters>({
    pageSize: 200,
    pageNumber: 1,
    culture: 'fi_FI',
  });

  const [editedSellVatData, setEditedSellVatData] =
    useState<SellVatDetailsData>({
      id: '',
      name: '',
      percentage: null,
      accountingGroup: '',
    });

  const initFetch = useCallback(() => {
    dispatch(getAllSellAccountingGroupsAsync(filtering));
    dispatch(getAllSellVatsAsync(filtering));
  }, [dispatch]);

  useEffect(() => {
    initFetch();
  }, [initFetch]);

  // Choose sell vats to edit by Id
  useEffect(() => {
    if (params.sellVatsId && sellvats.length > 0) {
      const getSellVatsId = _id =>
        sellvats.find(({ id }) => id === params.sellVatsId);
      if (getSellVatsId) {
        setEditedSellVatData(getSellVatsId);
      }
    }
  }, [params.sellVatsId, sellvats]);

  // Set view loading status
  useEffect(() => {
    if (
      sellvatsLoadingStatus !== LOADING_STATUS.LOADING &&
      editedSellVatData.id !== ''
    ) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
  }, [sellvatsLoadingStatus, editedSellVatData.id]);

  const accountingGroupsOptions = accountingGroups.map(item => (
    <option key={item.id} value={item.id}>
      {item.name}
    </option>
  ));

  // Handle save
  const saveSellVat = () => {
    dispatch(updateSellVatAsync(editedSellVatData));
  };

  const handleChangesellVat = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    if (editedSellVatData != null) {
      const storedSellVatData: SellVatDetailsData = {
        ...editedSellVatData,
        [event.target.name]: value,
      };
      setEditedSellVatData(storedSellVatData);
    }
  };

  const handleChangeSellVatAccountingGroup = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const accountingGroupGuid = accountingGroupsOptions.filter(
      itemv => value === itemv.key,
    );
    if (accountingGroupGuid != null) {
      const storededitedsellvat: SellVatDetailsData = {
        ...editedSellVatData,
        accountingGroup: accountingGroupGuid[0].key.toString(),
      };
      setEditedSellVatData(storededitedsellvat);
    } else {
      const storededitedsellvat = {
        ...editedSellVatData,
        purchaseVatId: null,
      };
      setEditedSellVatData(storededitedsellvat);
    }
  };

  return (
    <>
      <Helmet>
        <title>Myynti alv - Määrityksen tiedot</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          BackLink={backLinkUrl}
          Breadcrumbs={[
            'Kirjanpito ja Alv-määritykset',
            'Myynti Alv määritykset',
          ]}
          Title={`Alv-määrityksen tiedot: ${editedSellVatData.name}`}
        />

        <Loader IsLoading={isLoading} />

        {!isLoading && (
          <Container className="container-form mt-48">
            <Form.Group controlId="name" className="mb-40">
              <Form.Label>Nimi</Form.Label>
              <Form.Control
                autoComplete="off"
                value={editedSellVatData.name}
                onChange={handleChangesellVat}
                name="name"
              />
            </Form.Group>

            <Form.Group controlId="percentage" className="mb-40">
              <Form.Label>Prosentti %</Form.Label>
              <Form.Control
                autoComplete="off"
                value={editedSellVatData.percentage}
                onChange={handleChangesellVat}
                name="percentage"
              />
            </Form.Group>
            <Form.Group controlId="accountinggroup" className="mb-40">
              <Form.Label>Kirjanpitotilit</Form.Label>
              <Form.Select
                value={editedSellVatData.accountingGroup}
                onChange={handleChangeSellVatAccountingGroup}
                name="accountingGroupsOptions"
              >
                {accountingGroupsOptions}
              </Form.Select>
            </Form.Group>

            <ActionButtons
              CancelButtonText="Peruuta"
              CancelButtonUrl={backLinkUrl}
              HandleSave={saveSellVat}
            />
          </Container>
        )}
      </Container>
    </>
  );
};

export default SellVatsDetails;
